import * as Sentry from '@sentry/react';
import { config } from 'rxjs';

/**
 * Route RxJS's unhandled stream errors into Sentry with the raw errored value
 * attached (WA-3718 / Sentry WAWIASSIST-236).
 *
 * Why this exists: when an observable errors and the subscriber has no `error`
 * callback, RxJS 7 calls `ConsumerObserver.error` → `handleUnhandledError` →
 * `reportUnhandledError`, whose default branch is literally
 * `setTimeout(() => { throw err })` (rxjs/internal/util/reportUnhandledError).
 * That rethrow happens in a timer, so:
 *   - the stack points at the timer, not at the failing stream;
 *   - the raw value is thrown as-is, so a non-Error value (`undefined`) reaches
 *     `window.onerror` as a bare string. Firefox renders that as
 *     "uncaught exception: undefined", which Sentry can only record as a
 *     synthetic one-frame event (`/customer/general:1`) with no stack and no
 *     indication of which stream failed — unactionable by construction.
 * This codebase has ~590 `.subscribe(...)` call sites in `src/`, of which only a
 * handful use the object form that can carry an `error` callback, so the whole
 * app funnels into that default branch. `responseHandler` absorbs errors for the
 * streams it wraps (its `catchError` returns `of(model)`), which is why the
 * escape is rare rather than constant — but "rare and undiagnosable" is exactly
 * what WA-3718 was.
 *
 * Installing `config.onUnhandledError` replaces the `throw err` branch, so these
 * stop arriving as stackless `window.onerror` noise and arrive instead as a
 * grouped, tagged Sentry exception carrying the value that was thrown.
 */

const MAX_SERIALIZED_LENGTH = 200;

const truncate = (text: string): string =>
  text.length > MAX_SERIALIZED_LENGTH ? `${text.slice(0, MAX_SERIALIZED_LENGTH)}…` : text;

/**
 * Stable, low-cardinality label for the thrown value — used as the Sentry
 * grouping fingerprint, so `undefined` throws from different streams stay in one
 * issue instead of fragmenting across identical timer stacks.
 */
export function unhandledValueKind(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (value instanceof Error) return `Error:${value.name}`;
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

/**
 * Human-readable rendering of the thrown value for the Sentry event. Must never
 * throw: it runs on the reporting path, and a throw here would recreate the very
 * stackless global error this module exists to remove.
 */
export function describeUnhandledValue(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === 'string') return value === '' ? '"" (empty string)' : `"${truncate(value)}"`;
  if (typeof value === 'symbol' || typeof value === 'function' || typeof value === 'bigint') {
    return truncate(String(value));
  }
  try {
    const serialized = JSON.stringify(value);
    return serialized === undefined ? truncate(String(value)) : truncate(serialized);
  } catch {
    // Circular reference, a BigInt inside an object, or a throwing `toJSON`.
    return `[unserializable ${typeof value}]`;
  }
}

interface IInstallOptions {
  /**
   * Also install `config.onStoppedNotification`, which reports `next`/`error`/
   * `complete` delivered to an ALREADY-STOPPED subscriber. RxJS makes that a
   * silent no-op unless this hook is set (`handleStoppedNotification` in
   * rxjs/internal/Subscriber), so the class is invisible by default — it hides
   * genuine races such as an async `next` callback calling `subscriber.error()`
   * after `subscriber.complete()` has run. Kept out of production: it is a
   * development smoke detector, not user-facing telemetry.
   */
  reportStoppedNotifications?: boolean;
}

export function installRxJsUnhandledErrorReporting({
  reportStoppedNotifications = false,
}: IInstallOptions = {}): void {
  config.onUnhandledError = (err: unknown) => {
    try {
      const kind = unhandledValueKind(err);
      const description = describeUnhandledValue(err);

      Sentry.withScope((scope) => {
        // Distinguishes this transport from a genuine window-level throw, so
        // triage knows an RxJS stream failed rather than a render/effect.
        scope.setTag('errorTransport', 'rxjs.onUnhandledError');
        scope.setTag('rxjsUnhandledValueKind', kind);
        scope.setContext('rxjsUnhandledError', { valueKind: kind, value: description });

        if (err instanceof Error) {
          // A real Error carries its own stack and message — capture it as-is so
          // Sentry keeps both, and let default grouping apply.
          Sentry.captureException(err);
          return;
        }

        // A non-Error value has no stack of its own. Wrap it so the event has a
        // usable title and a deliberate fingerprint instead of grouping on the
        // interchangeable timer stack.
        const synthetic = new Error(`RxJS unhandled stream error: ${description}`);
        synthetic.name = 'RxJsUnhandledStreamError';
        scope.setFingerprint(['rxjs-unhandled-non-error', kind]);
        Sentry.captureException(synthetic);
      });

      // Preserve the console signal the replaced `throw err` used to give in dev.
      console.error('[rxjs] unhandled stream error:', err);
    } catch {
      // Swallow: this hook runs inside a timer, so throwing here would produce
      // exactly the stackless uncaught global error it was added to eliminate.
    }
  };

  if (reportStoppedNotifications) {
    config.onStoppedNotification = (notification) => {
      console.warn(
        `[rxjs] "${notification.kind}" notification delivered to an already-stopped subscriber — ` +
          'RxJS discards it silently. Likely an async callback resolving after complete/unsubscribe.',
        notification,
      );
    };
  }
}
